import React from 'react';
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';

export type SidebarItem = {
  key: string;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  selected?: boolean;
  color?: 'default' | 'error';
};

export interface AppSidebarProps {
  title?: string;
  drawerWidth?: number;
  collapsedWidth?: number;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  mobileOpen: boolean;
  onMobileClose: () => void;
  topItems: SidebarItem[];
  bottomItems?: SidebarItem[];
}

const AppSidebar: React.FC<AppSidebarProps> = ({
  title = 'Navigation',
  drawerWidth = 280,
  collapsedWidth = 76,
  collapsed,
  onToggleCollapsed,
  mobileOpen,
  onMobileClose,
  topItems,
  bottomItems = [],
}) => {
  const renderContent = (isMobile: boolean) => {
    const effectiveCollapsed = isMobile ? false : collapsed;

    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: effectiveCollapsed ? 'center' : 'space-between',
            gap: 1,
          }}
        >
          {!effectiveCollapsed && (
            <Typography variant="subtitle2" color="text.secondary">
              {title}
            </Typography>
          )}

          {!isMobile && (
            <Tooltip title={effectiveCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
              <IconButton size="small" onClick={onToggleCollapsed} aria-label="Toggle sidebar">
                {effectiveCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Divider />

        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <List disablePadding>
            {topItems.map((item) => (
              <Tooltip
                key={item.key}
                title={effectiveCollapsed ? item.label : ''}
                placement="right"
                disableHoverListener={!effectiveCollapsed}
              >
                <ListItemButton
                  selected={Boolean(item.selected)}
                  onClick={item.onClick}
                  sx={{
                    px: effectiveCollapsed ? 1.5 : 2,
                    py: 1.25,
                    justifyContent: effectiveCollapsed ? 'center' : 'flex-start',
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: effectiveCollapsed ? 'auto' : 40,
                      color: item.color === 'error' ? 'error.main' : 'inherit',
                      justifyContent: 'center',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {!effectiveCollapsed && (
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        color: item.color === 'error' ? 'error.main' : undefined,
                      }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            ))}
          </List>
        </Box>

        {bottomItems.length > 0 && (
          <>
            <Divider />
            <List disablePadding>
              {bottomItems.map((item) => (
                <Tooltip
                  key={item.key}
                  title={effectiveCollapsed ? item.label : ''}
                  placement="right"
                  disableHoverListener={!effectiveCollapsed}
                >
                  <ListItemButton
                    selected={Boolean(item.selected)}
                    onClick={item.onClick}
                    sx={{
                      px: effectiveCollapsed ? 1.5 : 2,
                      py: 1.25,
                      justifyContent: effectiveCollapsed ? 'center' : 'flex-start',
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: effectiveCollapsed ? 'auto' : 40,
                        color: item.color === 'error' ? 'error.main' : 'inherit',
                        justifyContent: 'center',
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    {!effectiveCollapsed && (
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                          color: item.color === 'error' ? 'error.main' : undefined,
                        }}
                      />
                    )}
                  </ListItemButton>
                </Tooltip>
              ))}
            </List>
          </>
        )}
      </Box>
    );
  };

  const desktopWidth = collapsed ? collapsedWidth : drawerWidth;

  return (
    <>
      {/* Mobile */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
            height: '100vh',
          },
        }}
      >
        {renderContent(true)}
      </Drawer>

      {/* Desktop */}
      <Drawer
        variant="permanent"
        open
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: desktopWidth,
            // Account for the top AppBar (Layout uses position="static")
            // so the fixed sidebar never overlaps the navbar area.
            top: (theme) => (typeof theme.mixins.toolbar.minHeight === 'number' ? theme.mixins.toolbar.minHeight : 64),
            height: (theme) => {
              const h = typeof theme.mixins.toolbar.minHeight === 'number' ? theme.mixins.toolbar.minHeight : 64;
              return `calc(100vh - ${h}px)`;
            },
            position: 'fixed',
            left: 0,
            overflow: 'hidden',
          },
        }}
      >
        {renderContent(false)}
      </Drawer>
    </>
  );
};

export default AppSidebar;


